// components/Dropdown.tsx
import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import { ChevronDown, ChevronUp } from "./icons";

export type Option = {
  label: string;
  value: string;
};

type DropdownProps = {
  value: string;
  options: Option[];
  placeholder?: string;
  onChange: (value: string) => void;
};
const Container = styled.div`
  position: relative;
  width: 100%;
`;

const SortIcon = styled.span`
  width: 16px;
  text-align: center;
  display: inline-block;
`;

const Header = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== "isOpen",
})<{ isOpen: boolean }>`
  padding: 10px 12px;
  outline: ${({ isOpen }) =>
    isOpen ? "2px solid #0C8CE9" : "1px solid #cbd5e1"};
  border-radius: 6px;
  font-size: 16px;
  background-color: white;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const List = styled.ul`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  max-height: 200px;
  margin: 0;
  padding: 0;
  list-style: none;
  background-color: white;
  border: 1px solid #cbd5e1;
  border-radius: 6px 6px 6px 6px;
  overflow-y: auto;
  z-index: 10;
`;

const Item = styled.li<{ selected: boolean }>`
  padding: 10px 12px;
  cursor: pointer;
  background-color: ${({ selected }) => (selected ? "#0C8CE9" : "white")};
  color: ${({ selected }) => (selected ? "white" : "black")};
  &:hover {
    background-color: #f6f7f8;
    color: black;
  }
`;

export default function Dropdown({
  value,
  options,
  placeholder,
  onChange,
}: DropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const selectedLabel =
    options.find((opt) => opt.value === value)?.label ||
    placeholder ||
    "선택하세요";

  useEffect(() => {
    const handleOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);

  useEffect(() => {
    if (!value && options.length > 0) {
      onChange(options[0].value);
    }
  }, [value, options, onChange]);

  return (
    <Container ref={containerRef}>
      <Header isOpen={isOpen} onClick={() => setIsOpen((prev) => !prev)}>
        <span>{selectedLabel}</span>
        <SortIcon>{isOpen ? <ChevronUp /> : <ChevronDown />}</SortIcon>
      </Header>
      {isOpen && (
        <List>
          {options.map((opt, idx) => (
            <Item
              key={idx}
              selected={opt.value === value}
              onClick={() => {
                onChange(opt.value);
                setIsOpen(false);
              }}
            >
              {opt.label}
            </Item>
          ))}
        </List>
      )}
    </Container>
  );
}
